/**
 * HotspotLayer Component
 *
 * Displays DEA (Digital Earth Australia) satellite hotspots on the map.
 * Hotspots are fetched from a static GeoJSON file served via CloudFront,
 * separate from the main events API.
 *
 * IMPORTANT: Hotspots are thermal anomalies detected by satellite.
 * They are NOT confirmed fires - they may be industrial activity,
 * controlled burns, or other heat sources.
 *
 * PERFORMANCE: the feed is 20k-54k points on a busy fire day. Rendering one
 * SVG <path> (or even one canvas-renderer CircleMarker) per point melts
 * mobile, because each point is also a React component + a Leaflet layer
 * object, all iterated on every pan/zoom. Instead we draw every point onto a
 * SINGLE canvas imperatively (one draw loop, zero per-point objects) and
 * hit-test clicks against the point array. No data is dropped - every
 * detection is drawn at full fidelity.
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import { Popup, useMap, Marker } from 'react-leaflet';
import L from 'leaflet';
import { useAuth } from '../contexts/AuthContext';
import { UnifiedEvent } from '../types/events';
import { fetchHotspotColumns } from '../workers/fetchHotspotColumns';
import { hotspotAt, type HotspotColumns, type HotspotFeatureLike } from '../workers/hotspotColumns';

// Hotspots GeoJSON URL - served from CloudFront
// This URL will be configured via environment variable once CloudFront is deployed
const HOTSPOTS_URL = import.meta.env.VITE_HOTSPOTS_URL || '';

// Refresh interval - matches ingestion (5 minutes)
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// Drawn circle radius (CSS px) and click hit tolerance (a little larger than
// the circle so taps near a point still register on touch).
const HOTSPOT_RADIUS = 8;
const HIT_TOLERANCE = 14;

// Hotspots are held as a structure-of-arrays (HotspotColumns) rather than an
// array of feature objects: the ~4MB feed (20k-54k points) is flattened in a
// worker and the numeric columns transfer zero-copy, so the main thread never
// pays to deserialize tens of thousands of nested objects. Popups/handlers
// rebuild a single feature via hotspotAt() on demand.

interface HotspotLayerProps {
  enabled?: boolean;
  onView3D?: (event: UnifiedEvent) => void;
  onRequestAuth?: () => void;
}

/**
 * Get color based on hotspot confidence level.
 * Higher confidence = more likely to be a real fire.
 */
function getHotspotColor(confidence: number): string {
  if (confidence >= 90) return '#dc2626'; // High confidence - red
  if (confidence >= 70) return '#f97316'; // Medium confidence - orange
  return '#fbbf24'; // Lower confidence - yellow
}

/**
 * Format relative time for display.
 */
function formatTimeAgo(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

/**
 * Convert a hotspot feature to a UnifiedEvent for 3D view.
 */
function hotspotToUnifiedEvent(feature: HotspotFeatureLike): UnifiedEvent {
  const [lon, lat] = feature.geometry.coordinates;
  const props = feature.properties;

  return {
    id: `hotspot-${props.id}`,
    type: 'HOTSPOT',
    title: `${props.satellite} Hotspot - ${props.region}`,
    location: { lat, lon },
    region: props.region,
    observedAt: props.observedAt,
    confidence: props.confidence,
    data: {
      satellite: props.satellite,
      sensor: props.sensor,
      confidence: props.confidence,
      confidencePercent: props.confidencePercent,
      tempCelsius: props.tempCelsius,
      power: props.power,
      region: props.region,
    },
  };
}

/**
 * HotspotLayer - satellite thermal anomaly detections drawn on a single
 * canvas overlay. Every point is rendered (no clustering, no downsampling).
 */
export default function HotspotLayer({ enabled = true, onView3D, onRequestAuth }: HotspotLayerProps) {
  const map = useMap();
  const { isAuthenticated } = useAuth();
  const [hotspots, setHotspots] = useState<HotspotColumns | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which hotspot's popup is open (id, not the feature, so it survives refetch)
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const canvasElRef = useRef<HTMLCanvasElement | null>(null);

  // Fetch hotspots GeoJSON
  useEffect(() => {
    if (!enabled || !HOTSPOTS_URL) {
      setHotspots(null);
      return;
    }

    const fetchHotspots = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch + parse + flatten to columns off the main thread — the numeric
        // columns transfer zero-copy, so the main thread never pays to parse or
        // deserialize the ~4MB feed (tens of thousands of points).
        const data = await fetchHotspotColumns(HOTSPOTS_URL, {
          Accept: 'application/geo+json, application/json',
        });
        setHotspots(data);
        console.log(`[HotspotLayer] Loaded ${data.count} hotspots`);
      } catch (err) {
        console.error('[HotspotLayer] Error fetching hotspots:', err);
        setError(err instanceof Error ? err.message : 'Failed to load hotspots');
      } finally {
        setIsLoading(false);
      }
    };

    // Initial fetch
    fetchHotspots();

    // Refresh — skip while the tab is hidden (avoids the ~4MB download + main-
    // thread parse in the background); refetch once on becoming visible again.
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchHotspots();
    }, REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (typeof document !== 'undefined' && !document.hidden) fetchHotspots();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);

  // Get current viewport bounds (debounced). Used only to bound the small
  // set of pulsing "recent" markers — the main circles are drawn straight
  // from the full array on the canvas, off live bounds.
  const [viewBounds, setViewBounds] = useState<L.LatLngBounds | null>(null);

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const updateBounds = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setViewBounds(map.getBounds());
      }, 150);
    };

    setViewBounds(map.getBounds());
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  // Recent + in-view hotspots get a pulsing ring. This set is small (15-min
  // recency window, viewport-bounded), so keeping them as animated HTML
  // markers is fine — only the bulk circles need the canvas.
  const recentVisibleHotspots = useMemo<number[]>(() => {
    if (!hotspots || !viewBounds) return [];
    const latBuffer = (viewBounds.getNorth() - viewBounds.getSouth()) * 0.2;
    const lngBuffer = (viewBounds.getEast() - viewBounds.getWest()) * 0.2;
    const buffered = L.latLngBounds(
      [viewBounds.getSouth() - latBuffer, viewBounds.getWest() - lngBuffer],
      [viewBounds.getNorth() + latBuffer, viewBounds.getEast() + lngBuffer]
    );
    const now = Date.now();
    const FIFTEEN_MIN_MS = 15 * 60 * 1000;
    const out: number[] = [];
    const { lat, lon, observedAtMs, count } = hotspots;
    for (let i = 0; i < count; i++) {
      const ms = observedAtMs[i];
      if (Number.isNaN(ms) || now - ms > FIFTEEN_MIN_MS) continue; // recency
      if (buffered.contains([lat[i], lon[i]])) out.push(i);
    }
    return out;
  }, [hotspots, viewBounds]);

  // ── Imperative canvas overlay ──────────────────────────────────────────
  // Draws every in-view hotspot circle on one canvas. Mirrors the proven
  // AirQualityLayer canvas lifecycle: CSS-transform the canvas during a pan
  // (cheap), redraw fully on moveend/zoomend (accurate), DPR-aware for retina.
  useEffect(() => {
    if (!enabled || !hotspots) {
      if (canvasElRef.current) {
        canvasElRef.current.remove();
        canvasElRef.current = null;
      }
      return;
    }

    let canvas = canvasElRef.current;
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.pointerEvents = 'none'; // clicks handled via map 'click'
      canvas.style.willChange = 'transform';
      canvasElRef.current = canvas;
      // Append to the OVERLAY PANE (below markers z600 & popups z700) instead
      // of the map container (which floated the canvas above popups and fire
      // markers). Positioned per-draw via L.DomUtil.setPosition in layer space.
      // (user feedback)
      map.getPanes().overlayPane.appendChild(canvas);
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cols = hotspots;
    const TWO_PI = Math.PI * 2;
    let zooming = false;

    const draw = () => {
      const size = map.getSize();
      const dpr = window.devicePixelRatio || 1;
      canvas!.width = size.x * dpr;
      canvas!.height = size.y * dpr;
      canvas!.style.width = `${size.x}px`;
      canvas!.style.height = `${size.y}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size.x, size.y);

      // Pin the canvas to the current viewport's top-left in LAYER coords so
      // container-point drawing aligns. The canvas then rides the map pane
      // during pans automatically (no manual translate needed).
      L.DomUtil.setPosition(canvas!, map.containerPointToLayerPoint([0, 0]));

      const bounds = map.getBounds().pad(0.1);
      ctx.lineWidth = 2;
      const { lat: lats, lon: lons, confidencePercent, count } = cols;
      for (let i = 0; i < count; i++) {
        const lat = lats[i], lon = lons[i];
        if (Number.isNaN(lat) || !bounds.contains([lat, lon])) continue;
        const pt = map.latLngToContainerPoint([lat, lon]);
        const color = getHotspotColor(confidencePercent[i]);
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, HOTSPOT_RADIUS, 0, TWO_PI);
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = color;
        ctx.fill();
        ctx.globalAlpha = 0.9;
        ctx.strokeStyle = color;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    };

    // No manual pan handler needed: the canvas lives in the overlay pane and
    // rides the map-pane transform during a drag, then redraws on moveend.
    // Hide during zoom (points can't rescale mid-animation); redraw on end.
    const onZoomStart = () => { zooming = true; canvas!.style.visibility = 'hidden'; };
    const onZoomEnd = () => { zooming = false; canvas!.style.visibility = 'visible'; draw(); };
    const onMoveEnd = () => { if (!zooming) draw(); };
    const onResize = () => draw();

    // Click hit-test: nearest in-view point within HIT_TOLERANCE opens its popup.
    const onClick = (e: L.LeafletMouseEvent) => {
      const cp = e.containerPoint;
      const bounds = map.getBounds().pad(0.1);
      let bestIdx = -1;
      let bestSq = HIT_TOLERANCE * HIT_TOLERANCE;
      const { lat: lats, lon: lons, count } = cols;
      for (let i = 0; i < count; i++) {
        const lat = lats[i], lon = lons[i];
        if (Number.isNaN(lat) || !bounds.contains([lat, lon])) continue;
        const pt = map.latLngToContainerPoint([lat, lon]);
        const dx = pt.x - cp.x;
        const dy = pt.y - cp.y;
        const sq = dx * dx + dy * dy;
        if (sq <= bestSq) {
          bestSq = sq;
          bestIdx = i;
        }
      }
      if (bestIdx >= 0) setSelectedId(cols.id[bestIdx]);
    };

    draw();
    map.on('moveend', onMoveEnd);
    map.on('zoomstart', onZoomStart);
    map.on('zoomend', onZoomEnd);
    map.on('resize', onResize);
    map.on('click', onClick);

    return () => {
      map.off('moveend', onMoveEnd);
      map.off('zoomstart', onZoomStart);
      map.off('zoomend', onZoomEnd);
      map.off('resize', onResize);
      map.off('click', onClick);
    };
  }, [enabled, hotspots, map]);

  // Remove the canvas entirely on unmount.
  useEffect(() => {
    return () => {
      if (canvasElRef.current) {
        canvasElRef.current.remove();
        canvasElRef.current = null;
      }
    };
  }, []);

  if (!enabled || !HOTSPOTS_URL) {
    return null;
  }

  if (isLoading && !hotspots) {
    return null; // Don't show loading indicator on map
  }

  if (error) {
    console.warn('[HotspotLayer] Error state:', error);
    return null; // Fail silently on map
  }

  // Pulsing div icon for recent hotspots. One ring (a second delayed ring
  // doubled compositor cost without adding signal). Animation pauses under
  // prefers-reduced-motion.
  const createPulsingIcon = (color: string) => {
    return L.divIcon({
      className: 'hotspot-pulse-container',
      html: `<div class="hotspot-pulse-ring" style="border-color: ${color};"></div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });
  };

  // Popup body for a hotspot. Rendered once (for the selected hotspot) rather
  // than one <Popup> per point.
  const renderPopupContent = (feature: HotspotFeatureLike) => {
    const props = feature.properties;
    const color = getHotspotColor(props.confidencePercent);
    return (
      <div className="hotspot-popup">
        {/* Header - colored banner */}
        <div style={{
          backgroundColor: color,
          color: props.confidencePercent >= 90 ? 'white' : 'black',
          padding: '10px 14px',
          borderRadius: '8px 8px 0 0',
        }}>
          <div style={{ fontSize: '10px', opacity: 0.9, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Satellite Hotspot
          </div>
          <div style={{ fontWeight: '600', fontSize: '14px' }}>
            {props.satellite} ({props.sensor})
          </div>
        </div>

        {/* Body content */}
        <div style={{ padding: '12px 14px' }}>
          {/* Confidence badge */}
          <div style={{ marginBottom: '10px' }}>
            <span style={{
              backgroundColor: color,
              color: props.confidencePercent >= 90 ? 'white' : 'black',
              padding: '3px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600',
            }}>
              {props.confidencePercent}% confidence
            </span>
          </div>

          {/* Details */}
          <div style={{ fontSize: '12px', lineHeight: '1.7' }}>
            {props.tempCelsius && (
              <div><strong>Temperature:</strong> {props.tempCelsius}°C</div>
            )}
            {props.power != null && props.power > 0 && (
              <div><strong>Fire Power:</strong> {props.power} MW</div>
            )}
            <div><strong>Region:</strong> {props.region}</div>
            <div><strong>Detected:</strong> {formatTimeAgo(props.observedAt)}</div>
          </div>

          {/* Disclaimer */}
          <div style={{
            marginTop: '12px',
            padding: '8px 10px',
            backgroundColor: '#fef3c7',
            borderRadius: '4px',
            fontSize: '10px',
            color: '#92400e',
            lineHeight: '1.5',
          }}>
            <strong>Note:</strong> Satellite hotspots detect thermal anomalies.
            Not all hotspots are fires - may be industrial or controlled burns.
          </div>

          {/* View in 3D Button */}
          {(onView3D || onRequestAuth) && (
            <button
              onClick={() => {
                if (isAuthenticated && onView3D) {
                  onView3D(hotspotToUnifiedEvent(feature));
                } else if (onRequestAuth) {
                  onRequestAuth();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                width: '100%',
                marginTop: '12px',
                padding: '10px 14px',
                backgroundColor: '#7c3aed',
                color: 'white',
                textAlign: 'center',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '12px',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span>🌐</span>
              {isAuthenticated ? 'View in 3D Terrain' : 'Sign in for 3D View'}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Resolve the open popup's hotspot by id (survives refetch). Find its index
  // in the columns, then rebuild the single feature object on demand.
  const selectedIdx = selectedId && hotspots ? hotspots.id.indexOf(selectedId) : -1;
  const selectedFeature = selectedIdx >= 0 && hotspots
    ? hotspotAt(hotspots, selectedIdx)
    : undefined;

  return (
    <>
      {/* CSS keyframes for the pulsing animation - injected once. Explicit
          pointer-events:none so the ring can't eat taps meant for the map. */}
      <style>{`
        .hotspot-pulse-container,
        .hotspot-pulse-container * {
          pointer-events: none !important;
        }
        .hotspot-pulse-container {
          background: transparent !important;
          border: none !important;
        }
        @keyframes hotspot-pulse {
          0% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 0.9;
          }
          100% {
            transform: translate(-50%, -50%) scale(2.5);
            opacity: 0;
          }
        }
        .hotspot-pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 3px solid;
          background: transparent;
          animation: hotspot-pulse 2s ease-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .hotspot-pulse-ring {
            animation: none;
            opacity: 0.6;
          }
        }
      `}</style>

      {/* Pulsing rings for recent + in-view hotspots (small, bounded set). The
          bulk circles are drawn on the imperative canvas overlay above. */}
      {hotspots && recentVisibleHotspots.map((i) => {
        const color = getHotspotColor(hotspots.confidencePercent[i]);
        return (
          <Marker
            key={hotspots.id[i]}
            position={[hotspots.lat[i], hotspots.lon[i]]}
            icon={createPulsingIcon(color)}
            interactive={false}
          />
        );
      })}

      {/* One shared popup for whichever hotspot was last clicked. */}
      {selectedFeature && (
        <Popup
          key={selectedFeature.properties.id}
          position={[
            selectedFeature.geometry.coordinates[1],
            selectedFeature.geometry.coordinates[0],
          ]}
          maxWidth={300}
          minWidth={240}
          className="hotspot-popup-wrapper"
          eventHandlers={{ remove: () => setSelectedId(null) }}
        >
          {renderPopupContent(selectedFeature)}
        </Popup>
      )}
    </>
  );
}
